export const PRODUCT_CSV_COLUMNS = [
  'sku',
  'product_name',
  'marketplace',
  'category',
  'selling_price',
  'quantity',
  'hpp',
  'seller_discount',
  'seller_voucher',
  'packing_cost',
  'affiliate_rate',
  'ads_rate',
  'free_shipping_xtra',
  'promo_xtra',
  'pre_order',
  'platform_campaign',
  'target_profit',
];

export const RESULT_CSV_COLUMNS = [
  ...PRODUCT_CSV_COLUMNS,
  'gross_sales',
  'net_product_value',
  'admin_fee',
  'program_fee',
  'affiliate_fee',
  'ads_fee',
  'order_processing_fee',
  'total_marketplace_fees',
  'total_hpp',
  'total_packing_cost',
  'net_payout',
  'net_profit',
  'margin_percentage',
  'profit_status',
  'safe_selling_price',
  'validation_errors',
];

const BOOLEAN_TRUE = new Set(['true', 'yes', '1', 'y']);
const BOOLEAN_FALSE = new Set(['false', 'no', '0', 'n']);

const normalizeHeader = (value) => String(value || '').replace(/^\uFEFF/, '').trim().toLowerCase();

const cleanText = (value) => String(value ?? '').trim();

const isEmptyRow = (row) => Object.values(row).every((value) => cleanText(value) === '');

const normalizeNumberText = (value) => {
  const stripped = cleanText(value)
    .replace(/^rp\.?/i, '')
    .replace(/\s/g, '')
    .replace(/[^\d.,-]/g, '');

  if (!stripped) {
    return '';
  }

  if (/^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(stripped)) {
    return stripped.replace(/\./g, '').replace(',', '.');
  }

  if (/^-?\d{1,3}(,\d{3})+(\.\d+)?$/.test(stripped)) {
    return stripped.replace(/,/g, '');
  }

  return stripped.replace(',', '.');
};

const parseNumber = (value, fieldLabel, warnings, defaultValue = 0) => {
  const text = normalizeNumberText(value);

  if (!text) {
    return defaultValue;
  }

  const number = Number(text);

  if (!Number.isFinite(number)) {
    warnings.push(`${fieldLabel} tidak valid, dipakai ${defaultValue}.`);
    return defaultValue;
  }

  return Math.max(0, number);
};

const parseQuantity = (value, warnings) => {
  const text = normalizeNumberText(value);
  const number = Number(text);

  if (!text || !Number.isFinite(number) || number <= 0) {
    if (cleanText(value)) {
      warnings.push('Quantity tidak valid, dipakai 1.');
    }

    return 1;
  }

  return Math.max(1, Math.floor(number));
};

const parsePercent = (value, fieldLabel, warnings) => {
  const number = parseNumber(value, fieldLabel, warnings, 0);

  return Math.min(number, 100);
};

const parseBoolean = (value, fieldLabel, warnings) => {
  const text = cleanText(value).toLowerCase();

  if (!text) {
    return false;
  }

  if (BOOLEAN_TRUE.has(text)) {
    return true;
  }

  if (BOOLEAN_FALSE.has(text)) {
    return false;
  }

  warnings.push(`${fieldLabel} tidak valid, dipakai false.`);
  return false;
};

const normalizeMarketplace = (value) => {
  const text = cleanText(value).toLowerCase().replace(/[\s-]/g, '_');

  if (text === 'shopee') {
    return 'shopee';
  }

  if (text === 'tiktok_shop' || text === 'tiktokshop') {
    return 'tiktokShop';
  }

  return '';
};

const normalizeCategory = (value, rules, warnings) => {
  const text = cleanText(value);
  const categories = rules?.categories || {};

  if (!text) {
    return 'general';
  }

  const directKey = text.toLowerCase();

  if (categories[directKey]) {
    return directKey;
  }

  const labelMatch = Object.entries(categories).find(([, category]) => category.label.toLowerCase() === directKey);

  if (labelMatch) {
    return labelMatch[0];
  }

  warnings.push(`Kategori "${text}" tidak ditemukan, dipakai general.`);
  return 'general';
};

const parseCsvRows = (text) => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const nextChar = text[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        field += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      row.push(field);
      field = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        index += 1;
      }

      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }

  if (field || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows;
};

const mapRawRow = (headers, values) =>
  headers.reduce((row, header, index) => {
    row[header] = values[index] ?? '';
    return row;
  }, {});

const normalizeProductRow = (rawRow, rowNumber, rules) => {
  const warnings = [];
  const errors = [];

  if (isEmptyRow(rawRow)) {
    errors.push('Baris kosong.');
  }

  const marketplace = normalizeMarketplace(rawRow.marketplace);

  if (!marketplace) {
    errors.push('Marketplace harus shopee, tiktok_shop, atau tiktokShop.');
  }

  const input = {
    sku: cleanText(rawRow.sku),
    productName: cleanText(rawRow.product_name),
    marketplace,
    category: normalizeCategory(rawRow.category, rules, warnings),
    sellingPrice: parseNumber(rawRow.selling_price, 'Selling price', warnings, 0),
    quantity: parseQuantity(rawRow.quantity, warnings),
    costOfGoods: parseNumber(rawRow.hpp, 'HPP', warnings, 0),
    sellerDiscount: parseNumber(rawRow.seller_discount, 'Seller discount', warnings, 0),
    sellerVoucher: parseNumber(rawRow.seller_voucher, 'Seller voucher', warnings, 0),
    packingCost: parseNumber(rawRow.packing_cost, 'Packing cost', warnings, 0),
    affiliateRate: parsePercent(rawRow.affiliate_rate, 'Affiliate rate', warnings),
    adsRate: parsePercent(rawRow.ads_rate, 'Ads rate', warnings),
    freeShippingXtra: parseBoolean(rawRow.free_shipping_xtra, 'Free Shipping XTRA', warnings),
    promoXtra: parseBoolean(rawRow.promo_xtra, 'Promo XTRA', warnings),
    preOrder: parseBoolean(rawRow.pre_order, 'Pre Order', warnings),
    platformCampaign: parseBoolean(rawRow.platform_campaign, 'Platform Campaign', warnings),
    targetProfit: parseNumber(rawRow.target_profit, 'Target profit', warnings, 0),
  };

  return {
    rowNumber,
    raw: rawRow,
    input,
    warnings: [...errors, ...warnings],
    isValid: errors.length === 0,
  };
};

export function parseProductCsv(text, rules) {
  const csvText = String(text || '');
  const fileWarnings = [];
  const parsedRows = parseCsvRows(csvText);
  const headerIndex = parsedRows.findIndex((row) => row.some((value) => cleanText(value) !== ''));

  if (headerIndex === -1) {
    return {
      headers: [],
      fileWarnings: ['File CSV kosong.'],
      rows: [],
    };
  }

  const headers = parsedRows[headerIndex].map(normalizeHeader);
  const missingColumns = PRODUCT_CSV_COLUMNS.filter((column) => !headers.includes(column));

  if (missingColumns.length > 0) {
    fileWarnings.push(`Kolom belum lengkap: ${missingColumns.join(', ')}.`);
  }

  const dataRows = parsedRows.slice(headerIndex + 1);
  const rows = dataRows.map((values, index) => {
    const rawRow = mapRawRow(headers, values);
    return normalizeProductRow(rawRow, headerIndex + index + 2, rules);
  });

  return {
    headers,
    fileWarnings,
    rows,
  };
}

const marketplaceToCsv = (marketplace) => (marketplace === 'tiktokShop' ? 'tiktok_shop' : marketplace);

const toCsvCell = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  const text = String(value);

  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
};

export function buildProductTemplateCsv() {
  const headerComment = '# Template CSV untuk Import Produk\n# PENTING: Nilai persentase (affiliate_rate, ads_rate) harus dalam format angka biasa (contoh: 10 untuk 10%, bukan 0.1)\n# Boolean: gunakan yes/no, true/false, 1/0\n';
  const sampleRows = [
    {
      sku: 'SHP-BEAUTY-001',
      product_name: 'Serum Brightening Sample',
      marketplace: 'shopee',
      category: 'beauty',
      selling_price: 100000,
      quantity: 1,
      hpp: 55000,
      seller_discount: 5000,
      seller_voucher: 0,
      packing_cost: 3000,
      affiliate_rate: 0,
      ads_rate: 0,
      free_shipping_xtra: 'yes',
      promo_xtra: 'no',
      pre_order: 'no',
      platform_campaign: 'no',
      target_profit: 25000,
    },
    {
      sku: 'TTS-BEAUTY-001',
      product_name: 'Face Mist Sample',
      marketplace: 'tiktok_shop',
      category: 'beauty',
      selling_price: 120000,
      quantity: 1,
      hpp: 65000,
      seller_discount: 0,
      seller_voucher: 5000,
      packing_cost: 3000,
      affiliate_rate: 10,
      ads_rate: 5,
      free_shipping_xtra: 'no',
      promo_xtra: 'no',
      pre_order: 'no',
      platform_campaign: 'yes',
      target_profit: 30000,
    },
  ];

  return [
    headerComment + PRODUCT_CSV_COLUMNS.join(','),
    ...sampleRows.map((row) => PRODUCT_CSV_COLUMNS.map((column) => toCsvCell(row[column])).join(',')),
  ].join('\n');
}

export function buildCalculatedResultsCsv(products) {
  const calculatedRows = products;

  const rows = calculatedRows.map((product) => {
    const input = product.input;
    const result = product.result;
    const validationErrors = product.isValid ? '' : product.warnings.join('; ');

    return {
      sku: input.sku,
      product_name: input.productName,
      marketplace: marketplaceToCsv(input.marketplace),
      category: input.category,
      selling_price: input.sellingPrice,
      quantity: input.quantity,
      hpp: input.costOfGoods,
      seller_discount: input.sellerDiscount,
      seller_voucher: input.sellerVoucher,
      packing_cost: input.packingCost,
      affiliate_rate: input.affiliateRate,
      ads_rate: input.adsRate,
      free_shipping_xtra: input.freeShippingXtra,
      promo_xtra: input.promoXtra,
      pre_order: input.preOrder,
      platform_campaign: input.platformCampaign,
      target_profit: input.targetProfit,
      gross_sales: result?.grossSales || '',
      net_product_value: result?.netProductValue || '',
      admin_fee: result?.adminFee || '',
      program_fee: result?.programFee || '',
      affiliate_fee: result?.affiliateFee || '',
      ads_fee: result?.adsFee || '',
      order_processing_fee: result?.orderProcessingFee || '',
      total_marketplace_fees: result?.totalMarketplaceFees || '',
      total_hpp: result?.totalHpp || '',
      total_packing_cost: result?.totalPackingCost || '',
      net_payout: result?.netPayout || '',
      net_profit: result?.netProfit || '',
      margin_percentage: result?.marginPercentage || '',
      profit_status: result?.profitStatus || 'Invalid',
      safe_selling_price: result?.safeSellingPrice || '',
      validation_errors: validationErrors,
    };
  });

  return [
    RESULT_CSV_COLUMNS.join(','),
    ...rows.map((row) => RESULT_CSV_COLUMNS.map((column) => toCsvCell(row[column])).join(',')),
  ].join('\n');
}
